export type PostLoginLocation = {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
};

/**
 * Address to put in the bar after Auth0's redirect callback, or null to leave it.
 *
 * Auth0 returns to the site root. The router mounts only after this callback and
 * reads the address bar then, so replaceState is enough to open a deep link
 * (a market page, charts) in the same document. Assigning location.href downloads
 * and boots the app a second time, which is the mobile loading-screen replay.
 *
 * Null when returnTo is missing, already the current address, or not this origin.
 */
export function postLoginReturnUrl(returnTo: string | undefined, location: PostLoginLocation): string | null {
  if (!returnTo) return null;

  let target: URL;
  try {
    target = new URL(returnTo, location.origin);
  } catch {
    return null;
  }
  if (target.origin !== location.origin) return null;

  const next = `${target.pathname}${target.search}${target.hash}`;
  const current = `${location.pathname}${location.search}${location.hash}`;
  if (next === current) return null;
  return next;
}

type HistoryWriter = {
  state: unknown;
  replaceState: (data: unknown, unused: string, url?: string | null) => void;
};

/**
 * Point the address bar at `url` without dropping the current history entry.
 *
 * createRouter() stores `{ __TSR_index: 0, key, __TSR_key }` on this entry.
 * replaceState with an empty object removes it. A missing index is not 0, so
 * TanStack treats the page as having somewhere to go back to, and Back leaves
 * the app for the pre-login entry.
 */
export function replaceUrlPreservingHistoryState(url: string, history: HistoryWriter, title: string): void {
  const current = history.state;
  const state = current && typeof current === "object" ? { ...current } : current;
  history.replaceState(state, title, url);
}
